<template>
  <div class="row">
    <div class="col-12">
      <div class="detail-breadcrumb">
        <h4><strong>Patient Detail</strong></h4>
        <a href="#" @click.prevent="$router.go(-1)" class="btn btn-link btn-icon-left"><i class="far fa-angle-left"></i>Back</a>
      </div>
      <hr class="hr-dark-color hr-thick" />
      <p class="loading" v-if="loading"><i class="far fa-fw fa-pulse fa-spinner"></i> loading patient...</p>

      <div class="row row-pt-header justify-content-between" v-if="!loading && patient">
        <div class="col-12 col-pt-name">
          <h4>Patient Name</h4>
          <h1>{{ patient.patientName || '(No Name)' }}</h1>
          <p v-if="patient.patnum">
            Patient Number: {{ patient.patnum }}
            <button v-if="!patnumCopied" @click="copyPatnum" class="btn btn-icon"><i class="far fa-copy"></i></button>
            <i v-else class="fas fa-check color-green"></i>
          </p>
          <hr class="hr-dark-color">
        </div> <!-- /.col -->
        <div class="col-4 col-md-auto">
          <h4>Priority</h4>
          <span
            v-if="patient.priorityScore || patient.priorityScore==0"
            class="badge badge-custom badge-custom-center fa-tooltip"
            :class="`badge-custom-${getPriorityColor(patient.priorityScore)}`"
          >
            {{ Math.round(patient.priorityScore) }}
            <span v-if="patient.priorityScoreExplanationSummary" class="tip">{{ patient.priorityScoreExplanationSummary }}</span>
            <span v-else class="tip">No explanation available</span>
          </span>
          <em v-else><small>TBD</small></em>
        </div> <!-- /.col -->
        <div class="col-4 col-md-auto col-stage">
          <h4>Path to Health</h4>
          <div class="d-flex align-items-center">
            <svg v-if="patient.stage" class="stage-icon" :width="patient.stage==6 ? '26px' : '29px'" height="26px" :viewBox="patient.stage==6 ? '0 0 50 50' : '0 0 65.091 50'" :style="{ fill: getStageColor(patient.stage) }" xmlns="http://www.w3.org/2000/svg">
              <path v-if="patient.stage !== 6" d="M65.091,25,47.844,7.752v7.077a25,25,0,1,0,0,20.341v7.077Z" transform="translate(0 0.001)"/>
              <circle v-else cx="25" cy="25" r="25" />
            </svg>
            <small v-if="patient.stage" class="stage-label-stacked">
              Step {{ patient.stage }} of 6<br>
              <strong>{{ getStageName(patient.stage) }}</strong>
            </small>
            <em v-else><small>TBD</small></em>
          </div>
        </div> <!-- /.col -->
        <div class="col-4 col-md-auto">
          <h4><i class="fas fa-fw fa-phone"></i></h4>
          <h4 v-if="patient.phone">{{ patient.phone}}</h4>
          <p v-else><em>--</em></p>
        </div> <!-- /.col -->
        <div class="col-4 col-md-auto">
          <h4>Last Appointment</h4>
          <p v-if="patient.mostRecentAppointmentDate">{{ patient.mostRecentAppointmentDate | prettyDateTimeShortTz }}</p>
          <p v-else><em><small>N/A</small></em></p>
        </div> <!-- /.col -->
        <div class="col-4 col-md-auto">
          <h4>Next Appointment</h4>
          <p v-if="patient.nextAppointmentDate">{{ patient.nextAppointmentDate | prettyDateTimeShortTz }}</p>
          <p v-else><em><small>N/A</small></em></p>
        </div> <!-- /.col -->
        <div class="col-4 col-md-auto">
          <h4>
            Scheduled?
            <i class="fal fa-info-circle fa-tooltip fa-tooltip-right outreach-scheduled-tooltip" aria-label="How scheduled status is determined">
              <span class="tip">
                <p>A patient is considered scheduled only when their next future scheduled appointment contains a qualifying procedure. Appointments scheduled for today do not count.</p>
                <p><strong>Stage 1:</strong> D0150 (comprehensive exam).</p>
                <p><strong>Stage 2:</strong> D0120 or D0150 (recare or comprehensive exam).</p>
                <p><strong>Stage 3:</strong> An advancing or rebuilding procedure.</p>
                <p><strong>Stage 4:</strong> An advancing procedure.</p>
                <p><strong>Stage 5:</strong> A rebuilding procedure.</p>
                <p><strong>Stage 6:</strong> D0120 or D0150 (recare or comprehensive exam).</p>
                <p>If the next appointment has no qualifying procedure, the patient is not marked as scheduled for their stage.</p>
              </span>
            </i>
          </h4>
          <p v-if="patient.pathToHealthStageIsScheduled === true">Yes</p>
          <p v-else-if="patient.pathToHealthStageIsScheduled === false">No</p>
          <p v-else><em><small>TBD</small></em></p>
        </div> <!-- /.col -->
      </div> <!-- /.row -->
      
      <p v-if="!loading && !patient"><em>Patient not found.</em></p>
      <hr class="hr-dark-color hr-thick" />

      <div v-if="!loading && patient" class="attempts row">
        <div class="attempts-list col-lg-8">
          <div class="d-flex justify-content-between align-items-center">
            <h3 class="snug">Scheduling Attempts</h3>
            <button v-if="!showClosedCases" @click="loadClosedCases" class="btn btn-link btn-sm">View Closed Cases</button>
            <button v-if="showClosedCases" @click="showClosedCases=false" class="btn btn-link btn-sm">Hide Closed Cases</button>
          </div>

          <hr class="hr-dark-color" />

          <!-- <p v-if="!activeCase"><em><small>No active case for this patient.</small></em></p> -->

          <ol v-if="activeCase" class="attempt-list list-unstyled">
            <li v-for="(attempt, index) in activeCase.attempts" :key="index">
              <p><strong>#{{ index + 1 }} &mdash; {{ attempt.timestamp | prettyDateTimeShortTz }} by {{ attempt.teamMemberInitials || '(unknown)' }}</strong></p>
              <p class="snug">Reason: {{ attempt.reason }}</p>
            </li>
            <li v-if="!activeCase.attempts.length"><em><small>No attempts logged yet.</small></em></li>
          </ol>

          <div v-if="addingAttempt" class="attempt-form">
            <hr class="hr-dark-color" />
            <h4>Add Attempt</h4>
            <div class="form-group">
              <!-- <label for="attemptReason" class="small mb-1">Reason</label> -->
              <textarea id="attemptReason" v-model="newAttempt.reason" class="form-control" rows="2" placeholder="Reason"></textarea>
            </div>
            <div class="form-group">
              <!-- <label for="attemptTeamMember" class="small mb-1">Team Member</label> -->
              <team-member-select
                id="attemptTeamMember"
                v-model="newAttempt.teamMemberId"
                @member-selected="newAttempt.teamMemberInitials = $event ? $event.initials : ''"
              />
            </div>
            <button @click="saveAttempt" class="btn btn-green-fade" :disabled="savingAttempt || !newAttempt.reason || !newAttempt.teamMemberId">Save Attempt</button>
            <button @click="cancelAddAttempt" class="btn btn-link">Cancel</button>
          </div>

          <div v-if="showClosedCases" class="closed-cases">
            <h4 class="snug">Closed Cases</h4>
            <hr class="hr-dark-color" />
            <p v-if="!closedCases.length"><em><small>No closed cases.</small></em></p>
            <div v-for="closedCase in closedCases" :key="closedCase.id" class="closed-case">
              <p class="case-closed-title snug"><strong>{{ closedCase.status === 'completed' ? 'Completed' : "Marked Don't Contact" }}</strong> &mdash; opened {{ closedCase.openedAt | prettyDateShortTz }}, closed {{ closedCase.closedAt | prettyDateShortTz }} by {{ closedCase.closedByInitials || '(unknown)' }}</p>
              <p class="case-closed-reason" v-if="closedCase.closeReason">Reason: {{ closedCase.closeReason }}</p>
              <p class="case-closed-attempts">Attempts:</p>
              <ol class="attempt-list list-unstyled">
                <li v-for="(attempt, index) in closedCase.attempts" :key="index">
                  <p><strong>#{{ index + 1 }} &mdash; {{ attempt.timestamp | prettyDateTimeShortTz }} by {{ attempt.teamMemberInitials || '(unknown)' }}</strong></p>
                  <p class="snug">Reason: {{ attempt.reason }}</p>
                </li>
              </ol>
            </div>
          </div>
        </div> <!-- /.col -->

        <!-- placeholder for assigned to & snooze -->
        <div class="case-settings col-lg-4">
          <div class="case-settings-container">
            <div>
              <h4><strong>Last Contacted</strong></h4>
              <p v-if="patient.lastContactedDate">{{ patient.lastContactedDate | prettyDateTimeShortTz }}</p>
              <p v-else><em><small>Not contacted yet</small></em></p>
            </div>
            <div>
              <h4><strong>Assigned To</strong></h4>
              <div class="d-flex align-items-center case-settings-assign-to">
                <team-member-select
                  id="assignedToTeamMember"
                  v-model="assignedToTeamMemberId"
                  @member-selected="assignedToTeamMemberInitials = $event ? $event.initials : ''"
                />
                <button
                  @click="saveAssignedTo"
                  class="btn btn-icon btn-save-assign"
                  :disabled="savingAssignedTo || !assignedToTeamMemberId"
                ><i class="fas fa-check"></i></button>
                <button
                  v-if="assignedToHasChanges"
                  @click="cancelAssignedTo"
                  class="btn btn-icon btn-cancel-assign"
                ><i class="far fa-ban"></i></button>
              </div>
            </div>
            <div>
              <h4><strong>{{ patient.snoozedUntil ? 'Snoozed Until' : 'Set Snooze' }}</strong></h4>
              <div class="d-flex align-items-end case-settings-snooze">
                <div>
                  <select v-model="snoozeOption" class="form-control">
                    <option value="" disabled>{{ patient.snoozedUntil ? formatSnoozeDate(patient.snoozedUntil) : 'Snooze' }}</option>
                    <option value="1_day">1 Day</option>
                    <option value="1_week">1 Week</option>
                    <option value="2_weeks">2 Weeks</option>
                    <option value="custom">Select Date</option>
                  </select>
                  <!-- TODO: temporary native input until a themeable date+time picker library is chosen -->
                  <input
                    v-if="snoozeOption === 'custom'"
                    type="datetime-local"
                    v-model="snoozeCustomDate"
                    class="form-control form-control-datetime"
                  />
                </div>
                <div>
                  <button
                    @click="saveSnooze"
                    class="btn btn-icon btn-save-assign"
                    :disabled="savingSnooze || !snoozeOption || (snoozeOption === 'custom' && !snoozeCustomDate)"
                  ><i class="fas fa-check"></i></button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div> <!-- /.attempts -->

      <hr class="hr-dark-color" />

      <list-errors :errors="errors" />

      <div class="d-flex justify-content-between align-items-center attempt-actions">
        <template v-if="pendingCloseAction">
          <div class="close-case-form">
            <h4>{{ pendingCloseAction === 'completed' ? 'Mark Complete' : "Mark As Don't Contact" }}</h4>
            <div class="form-group" v-if="pendingCloseAction === 'do_not_contact'">
              <!-- <label for="closeCaseReason" class="small mb-1">Reason</label> -->
              <textarea id="closeCaseReason" v-model="closeAction.reason" class="form-control" rows="2" placeholder="Reason"></textarea>
            </div>
            <div class="form-group">
              <!-- <label for="closeCaseTeamMember" class="small mb-1">Team Member</label> -->
              <team-member-select
                id="closeCaseTeamMember"
                v-model="closeAction.teamMemberId"
                @member-selected="closeAction.teamMemberInitials = $event ? $event.initials : ''"
              />
            </div>
            <button @click="confirmCloseCase" class="btn btn-confirm" :class="pendingCloseAction === 'completed' ? 'btn-green' : 'btn-salmon'" :disabled="!closeAction.teamMemberId || (pendingCloseAction === 'do_not_contact' && !closeAction.reason)">
              Save
            </button>
            <button @click="cancelCloseAction" class="btn btn-link">Cancel</button>
          </div>
        </template>
        <template v-else>
          <button v-if="activeCase" @click="startCloseAction('do_not_contact')" class="btn btn-salmon btn-icon-left"><i class="far fa-ban"></i>Mark As Don't Contact</button>
          <span v-else></span>
          <div>
            <button v-if="!addingAttempt" @click="startAddAttempt" class="btn btn-light-outline btn-icon-left"><i class="far fa-fw fa-plus-square"></i>Add Attempt</button>
            <button v-if="activeCase" @click="startCloseAction('completed')" class="btn btn-green btn-icon-left"><i class="far fa-check"></i>Mark Complete</button>
          </div>
        </template>
      </div> <!-- /.attempt-actions -->

    </div> <!-- /.col-12 -->
  </div> <!-- /.row -->
</template>

<script>
import Vue from 'vue'
import Firebase from 'firebase/compat/app'
import moment from 'moment-timezone'
import {mapGetters} from 'vuex'
import ListErrors from '../components/ListErrors.vue'
import TeamMemberSelect from '../components/TeamMemberSelect.vue'

export default {
  components: {
    ListErrors, TeamMemberSelect,
  },
  data() {
    return {
      loading: false,
      errors: [],
      patient: null,
      activeCase: null,
      closedCases: [],
      showClosedCases: false,
      addingAttempt: false,
      savingAttempt: false,
      pendingCloseAction: null,
      closeAction: {
        teamMemberId: '',
        teamMemberInitials: '',
        reason: '',
      },
      newAttempt: {
        reason: '',
        teamMemberId: '',
        teamMemberInitials: '',
      },
      assignedToTeamMemberId: '',
      assignedToTeamMemberInitials: '',
      savingAssignedTo: false,
      patnumCopied: false,
      snoozeOption: '',
      snoozeCustomDate: '',
      savingSnooze: false,
      stageNames: {
        1: 'Emergency Relief',
        2: 'Understand Each Other Fully',
        3: 'Make a Plan Together',
        4: 'Get Active Disease Under Control',
        5: 'Rebuild for the Long Run',
        6: 'Stay Healthy Together',
      },
    }
  },
  computed: {
    ...mapGetters(['custom_claims', 'user']),
    customerId() {
      return this.custom_claims?.customerId || null
    },
    locationId() {
      return this.custom_claims?.authorized_location || null
    },
    patNum() {
      return this.$route.params.patNum
    },
    assignedToHasChanges() {
      return this.assignedToTeamMemberId !== (this.activeCase?.assignedToId || '')
    },
  },
  watch: {
    custom_claims(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.loadPatient()
      }
    },
    patNum() {
      this.loadPatient()
    },
  },
  methods: {
    currentTimestamp() {
      return moment.tz('America/Chicago').unix()
    },
    formatSnoozeDate(timestamp) {
      return Vue.filter('prettyDateTimeShortTz')(timestamp)
    },
    async saveSnooze() {
      if (!this.snoozeOption) {
        return
      }

      let snoozedUntil = null

      if (this.snoozeOption === 'custom') {
        if (!this.snoozeCustomDate) {
          this.errors = ['Please select a date and time.']
          return
        }
        snoozedUntil = moment.tz(this.snoozeCustomDate, 'America/Chicago').unix()
      } else {
        const durationByOption = {
          '1_day': [1, 'days'],
          '1_week': [1, 'weeks'],
          '2_weeks': [2, 'weeks'],
        }
        const [amount, unit] = durationByOption[this.snoozeOption]
        snoozedUntil = moment.tz('America/Chicago').add(amount, unit).unix()
      }

      this.savingSnooze = true
      this.errors = []

      try {
        const patientRef = this.patientRef()
        const casesRef = this.casesCollectionRef()
        if (!patientRef || !casesRef) {
          return
        }

        if (this.activeCase) {
          await patientRef.update({ snoozedUntil })
        } else {
          const caseRef = casesRef.doc()
          const now = this.currentTimestamp()
          await caseRef.set({
            status: 'open',
            openedAt: now,
            openedBy: this.user?.uid || null,
            openedReason: 'manual',
            closedAt: null,
            closedBy: null,
            attempts: [],
            pathToHealthStageNum: Number.isInteger(this.patient.pathToHealthStageNum) ? this.patient.pathToHealthStageNum : null,
            unscheduledProcAmount: Number(this.patient.pathToHealthStageUnscheduledProcAmount) || 0,
          })
          await patientRef.update({
            activeCaseId: caseRef.id,
            contactStatus: 'open',
            hasCompletedCase: false,
            completedAt: null,
            snoozedUntil,
          })
        }

        await this.loadActiveCase()
        await this.refreshPatient()
        this.snoozeOption = ''
        this.snoozeCustomDate = ''
      } catch (err) {
        console.error('Failed to save snooze date:', err)
        this.errors = ['Failed to save snooze date. Please try again.']
      } finally {
        this.savingSnooze = false
      }
    },
    async copyPatnum() {
      if (!this.patient?.patnum) {
        return
      }

      if (!navigator.clipboard || !navigator.clipboard.writeText) {
        this.errors = ['Copy to clipboard is not available in this browser/context.']
        return
      }

      try {
        await navigator.clipboard.writeText(String(this.patient.patnum))
        this.patnumCopied = true
        setTimeout(() => { this.patnumCopied = false }, 2000)
      } catch (err) {
        console.error('Failed to copy patient number:', err)
      }
    },
    getPriorityColor(score) {
      if (score === null || score === undefined) {
        return 'dark'
      }
      if (score >= 15) {
        return 'salmon'
      } else if (score >= 10) {
        return 'orange'
      } else if (score >= 5) {
        return 'yellow'
      } else if (score >= 2) {
        return 'blue'
      } else {
        return 'green'
      }
    },
    getStageName(stage) {
      if (stage === null || stage === undefined) {
        return null
      }
      return this.stageNames[stage] || `Stage ${stage}`
    },
    getStageColor(stage) {
      if (stage === null || stage === undefined) {
        return '#e7e9eb' // @dim-color
      }
      switch (stage) {
        case 1:
          return '#EE404A' // @color-renken-red
        case 2:
          return '#F58915' // @color-renken-orange
        case 3:
          return '#F6DF4E' // @color-renken-yellow
        case 4:
          return '#8DAD3C' // @color-renken-green
        case 5:
          return '#6B86B2' // @color-renken-blue
        case 6:
          return '#B63E97' // @color-renken-magenta
        default:
          return '#e7e9eb' // @dim-color for unknown stages
      }
    },
    mapPatientDoc(doc) {
      const data = doc.data() || {}
      return {
        id: doc.id,
        patnum: data.patnum,
        patientName: data.patientName || '',
        phone: data.phone || '',
        priorityScore: data.priorityScore,
        priorityScoreExplanationSummary: data.priorityScoreExplanationSummary || '',
        mostRecentAppointmentDate: data.mostRecentAppointmentDate,
        nextAppointmentDate: data.nextAppointmentDate,
        stage: data.pathToHealthStageNum || null,
        pathToHealthStageNum: data.pathToHealthStageNum,
        pathToHealthStageIsScheduled: data.pathToHealthStageIsScheduled,
        pathToHealthStageUnscheduledProcAmount: data.pathToHealthStageUnscheduledProcAmount,
        activeCaseId: data.activeCaseId || null,
        hasCompletedCase: data.hasCompletedCase === true,
        completedAt: data.completedAt || null,
        lastContactedDate: data.lastContactedDate || null,
        snoozedUntil: data.snoozedUntil || null,
      }
    },
    patientRef() {
      if (!this.customerId || !this.locationId || !this.patNum) {
        return null
      }

      return Vue.firestore
        .collection('customers')
        .doc(this.customerId)
        .collection('locations')
        .doc(this.locationId)
        .collection('patients')
        .doc(String(this.patNum))
    },
    casesCollectionRef() {
      const patientRef = this.patientRef()
      return patientRef ? patientRef.collection('cases') : null
    },
    mapCaseDoc(doc) {
      const data = doc.data() || {}
      return {
        id: doc.id,
        status: data.status,
        openedAt: data.openedAt,
        openedBy: data.openedBy,
        closedAt: data.closedAt,
        closedBy: data.closedBy,
        closedByInitials: data.closedByInitials || '',
        closeReason: data.closeReason || '',
        assignedToId: data.assignedToId || '',
        assignedToInitials: data.assignedToInitials || '',
        attempts: data.attempts || [],
      }
    },
    async loadActiveCase() {
      this.activeCase = null

      const casesRef = this.casesCollectionRef()
      if (!casesRef) {
        return
      }

      const snapshot = await casesRef.where('status', '==', 'open').limit(1).get()
      if (!snapshot.empty) {
        this.activeCase = this.mapCaseDoc(snapshot.docs[0])
      }

      this.assignedToTeamMemberId = this.activeCase?.assignedToId || ''
      this.assignedToTeamMemberInitials = this.activeCase?.assignedToInitials || ''
    },
    async refreshPatient() {
      const patientRef = this.patientRef()
      if (!patientRef) {
        return
      }

      const doc = await patientRef.get()
      if (doc.exists) {
        this.patient = this.mapPatientDoc(doc)
      }
    },
    async loadClosedCases() {
      this.errors = []

      try {
        const casesRef = this.casesCollectionRef()
        if (!casesRef) {
          return
        }

        const snapshot = await casesRef.where('status', 'in', ['do_not_contact', 'completed']).get()
        this.closedCases = snapshot.docs.map((doc) => this.mapCaseDoc(doc))
          .sort((left, right) => (right.openedAt || 0) - (left.openedAt || 0))
        this.showClosedCases = true
      } catch (err) {
        console.error('Failed to load closed cases:', err)
        this.errors = ['Failed to load closed cases. Please try again.']
      }
    },
    startAddAttempt() {
      this.pendingCloseAction = null
      this.newAttempt = { reason: '', teamMemberId: '', teamMemberInitials: '' }
      this.addingAttempt = true
    },
    cancelAddAttempt() {
      this.addingAttempt = false
      this.newAttempt = { reason: '', teamMemberId: '', teamMemberInitials: '' }
      this.errors = []
    },
    startCloseAction(status) {
      this.addingAttempt = false
      this.closeAction = { teamMemberId: '', teamMemberInitials: '', reason: '' }
      this.pendingCloseAction = status
      this.errors = []
    },
    cancelCloseAction() {
      this.pendingCloseAction = null
      this.closeAction = { teamMemberId: '', teamMemberInitials: '', reason: '' }
      this.errors = []
    },
    async saveAttempt() {
      const reason = this.newAttempt.reason.trim()
      const teamMemberId = this.newAttempt.teamMemberId
      const teamMemberInitials = this.newAttempt.teamMemberInitials

      if (!teamMemberId) {
        this.errors = ['Please select a team member.']
        return
      }
      if (reason.length < 3) {
        this.errors = ['Please enter a reason that is at least 3 characters long.']
        return
      }

      this.savingAttempt = true
      this.errors = []
      const now = this.currentTimestamp()

      const attempt = {
        reason,
        timestamp: now,
        teamMemberId,
        teamMemberInitials,
      }

      try {
        const patientRef = this.patientRef()
        const casesRef = this.casesCollectionRef()
        if (!patientRef || !casesRef) {
          return
        }

        if (this.activeCase) {
          await casesRef.doc(this.activeCase.id).update({
            attempts: Firebase.firestore.FieldValue.arrayUnion(attempt),
          })
          await patientRef.update({ lastContactedDate: now })
        } else {
          const caseRef = casesRef.doc()
          await caseRef.set({
            status: 'open',
            openedAt: now,
            openedBy: this.user?.uid || null,
            openedReason: 'manual',
            closedAt: null,
            closedBy: null,
            attempts: [attempt],
            pathToHealthStageNum: Number.isInteger(this.patient.pathToHealthStageNum) ? this.patient.pathToHealthStageNum : null,
            unscheduledProcAmount: Number(this.patient.pathToHealthStageUnscheduledProcAmount) || 0,
          })
          await patientRef.update({
            activeCaseId: caseRef.id,
            contactStatus: 'open',
            hasCompletedCase: false,
            completedAt: null,
            lastContactedDate: now,
            snoozedUntil: null,
          })
        }

        await this.loadActiveCase()
        await this.refreshPatient()
        this.cancelAddAttempt()
      } catch (err) {
        console.error('Failed to save attempt:', err)
        this.errors = ['Failed to save attempt. Please try again.']
      } finally {
        this.savingAttempt = false
      }
    },
    async saveAssignedTo() {
      const assignedToId = this.assignedToTeamMemberId
      const assignedToInitials = this.assignedToTeamMemberInitials

      if (!assignedToId) {
        this.errors = ['Please select a team member to assign.']
        return
      }

      this.savingAssignedTo = true
      this.errors = []
      const now = this.currentTimestamp()

      try {
        const patientRef = this.patientRef()
        const casesRef = this.casesCollectionRef()
        if (!patientRef || !casesRef) {
          return
        }

        if (this.activeCase) {
          await casesRef.doc(this.activeCase.id).update({ assignedToId, assignedToInitials })
        } else {
          const caseRef = casesRef.doc()
          await caseRef.set({
            status: 'open',
            openedAt: now,
            openedBy: this.user?.uid || null,
            openedReason: 'manual',
            closedAt: null,
            closedBy: null,
            attempts: [],
            pathToHealthStageNum: Number.isInteger(this.patient.pathToHealthStageNum) ? this.patient.pathToHealthStageNum : null,
            unscheduledProcAmount: Number(this.patient.pathToHealthStageUnscheduledProcAmount) || 0,
            assignedToId,
            assignedToInitials,
          })
          await patientRef.update({
            activeCaseId: caseRef.id,
            contactStatus: 'open',
            hasCompletedCase: false,
            completedAt: null,
            snoozedUntil: null,
          })
        }

        await this.loadActiveCase()
        await this.refreshPatient()
      } catch (err) {
        console.error('Failed to assign team member:', err)
        this.errors = ['Failed to assign team member. Please try again.']
      } finally {
        this.savingAssignedTo = false
      }
    },
    cancelAssignedTo() {
      this.assignedToTeamMemberId = this.activeCase?.assignedToId || ''
      this.assignedToTeamMemberInitials = this.activeCase?.assignedToInitials || ''
      this.errors = []
    },
    async confirmCloseCase() {
      if (!this.activeCase || !this.pendingCloseAction) {
        return
      }

      const status = this.pendingCloseAction
      const teamMemberId = this.closeAction.teamMemberId
      const teamMemberInitials = this.closeAction.teamMemberInitials
      const reason = this.closeAction.reason.trim()

      if (!teamMemberId) {
        this.errors = ['Please select a team member.']
        return
      }
      if (status === 'do_not_contact' && reason.length < 3) {
        this.errors = ["Please enter a reason that is at least 3 characters long."]
        return
      }

      this.errors = []
      const now = this.currentTimestamp()

      try {
        const patientRef = this.patientRef()
        const casesRef = this.casesCollectionRef()
        if (!patientRef || !casesRef) {
          return
        }

        await casesRef.doc(this.activeCase.id).update({
          status,
          closedAt: now,
          closedBy: teamMemberId,
          closedByInitials: teamMemberInitials,
          closeReason: status === 'do_not_contact' ? reason : null,
        })
        await patientRef.update({
          activeCaseId: null,
          contactStatus: status === 'completed' ? 'none' : 'do_not_contact',
          hasCompletedCase: status === 'completed' ? true : this.patient.hasCompletedCase === true,
          completedAt: status === 'completed' ? now : this.patient.completedAt || null,
          lastContactedDate: now,
          snoozedUntil: null,
        })

        this.cancelCloseAction()
        await this.loadActiveCase()
        await this.refreshPatient()
      } catch (err) {
        console.error('Failed to close case:', err)
        this.errors = ['Failed to update case. Please try again.']
      }
    },
    async loadPatient() {
      this.loading = true
      this.errors = []
      this.patient = null
      this.activeCase = null
      this.closedCases = []
      this.showClosedCases = false

      try {
        const patientRef = this.patientRef()
        if (!patientRef) {
          return
        }

        const doc = await patientRef.get()
        if (doc.exists) {
          this.patient = this.mapPatientDoc(doc)
          await this.loadActiveCase()
        }
      } catch (err) {
        console.error('Failed to load patient:', err)
        this.errors = ['Failed to load patient. Please try again.']
      } finally {
        this.loading = false
      }
    },
  },
  mounted() {
    this.loadPatient()
  },
}
</script>
<style lang="less" scoped>
@import "../assets/less/variables.less";

.detail-breadcrumb {
  // margin-top: -20px;

  h4 {
    margin: 0 0 10px;
  }
}
.detail-breadcrumb + hr {
  margin: 50px 0 30px;
}
.stage-icon {
  margin-right: 5px;
}
.stage-label-stacked {
  line-height: 14px;
  max-width: 150px;

  > strong {
    display: inline-block;
    margin-top: 2px;
  }
}
table td svg + span {
  vertical-align: middle;
}
.attempt-list {
  padding-left: 20px;
}
.attempt-list li {
  margin-bottom: 10px;
}
.attempt-form {
  max-width: 500px;
  margin-bottom: 15px;

  .btn + .btn {
    margin-left: 10px;
  }
}
.close-case-form {
  max-width: 500px;
  width: 100%;
}
.closed-cases {
  margin-bottom: 15px;
}
.closed-case {
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 1px solid @dim-color;

  .case-closed-title + .case-closed-attempts {
    margin-top: 20px;
  }
}
.attempt-actions {
  margin-top: 15px;

  .btn + .btn {
    margin-left: 30px;
  }
}
.row-pt-header {
  h4 {
    margin: 0 0 15px 0;
    font-weight: 600;
  }
  h1,
  p {
    margin: 0;
  }
  // .col-pt-name {
  //   margin-bottom: 20px;
  //   padding-bottom: 20px;
  //   border-bottom: 1px solid @dim-color;
  // }
  .col-pt-name + hr {
    // margin: 20px 0;
  }
  .col-pt-name h4 {
    margin-bottom: 5px;
  }
  .col-pt-name h1 {
    margin-bottom: 5px;
  }
  .col-pt-name .btn-icon {
    margin-left: 5px;
    padding: 0 0 5px 0;
  }
  .col-stage h4 {
    margin-bottom: 5px;
  }
}
.row-pt-header + hr {
  margin-bottom: 0;
  margin-top: 30px;
}
.attempts + hr {
  margin-top: 0;
  margin-bottom: 30px;
}
.attempts {
  .attempts-list {
    padding-top: 40px;
    padding-bottom: 40px;

    hr {
      margin-top: 20px;
      margin-bottom: 20px;
    }
    ol.attempt-list {
      margin-bottom: 20px;
      padding: 0;

      > li {
        margin-bottom: 30px;
        padding: 0;

        > p:first-child {
          margin-bottom: 10px;
        }
      }
    }
  }
  .case-settings {
    h4 {
      margin-bottom: 5px;
    }
    > .case-settings-container {
      padding: 40px;
      background-color: fadeout(@major-color, 80%);
      height: 100%;

      > div {
        margin-bottom: 30px;
      }
      > div:last-child {
        margin-bottom: 0;
      }

      .case-settings-assign-to {
        /deep/ select {
          // flex-grow: 1;
          margin-bottom: 0 !important;
        }
        .btn-save-assign {
          margin-left: 10px;
          padding: 0;
        }
        .btn-cancel-assign {
          margin-left: 5px;
          padding: 0;
        }
      }
      .case-settings-snooze {
        .form-control {
          margin-bottom: 0;
        }
        .form-control.form-control-datetime {
          margin-top: 10px !important;
        }
      }
    }
  }
   
  
}
</style>
